'use client';

import type { Sort } from '@ria-client/advisor-search/constants';
import { Button } from '@ria-client/tw-shadcn/components/button';
import type { Ref } from 'react';

import { FilterListIcon } from '../icons';
import { ResultsSortMenu } from './ResultsSortMenu';

export interface ResultsToolbarProps {
  filterButtonRef?: Ref<HTMLButtonElement>;
  activeFilterCount: number;
  onOpenFilters: () => void;
  sort: Sort;
  onSortChange: (sort: Sort) => void;
  isLoading?: boolean;
}

export function ResultsToolbar({
  filterButtonRef,
  activeFilterCount,
  onOpenFilters,
  sort,
  onSortChange,
  isLoading = false,
}: ResultsToolbarProps) {
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="flex w-full items-center border-b border-border bg-finder-results-toolbar px-4 py-3 sm:px-6">
      <div className="mx-auto flex w-full max-w-[1248px] items-center justify-between">
        <div className="relative">
          <Button
            ref={filterButtonRef}
            type="button"
            variant="outline"
            fullWidth={false}
            disabled={isLoading}
            aria-label={buildAriaLabel(activeFilterCount)}
            onClick={onOpenFilters}
            className="h-[34px] rounded-full border-border bg-background px-5 py-0 font-semibold normal-case hover:border-foreground hover:bg-background"
          >
            <FilterListIcon className="size-5 -ml-1" />
            Filters
          </Button>
          {hasActiveFilters ? (
            <span
              aria-hidden="true"
              className="absolute top-0 right-0 flex h-5 min-w-5 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-primary px-1.5 text-xs leading-none font-bold text-primary-foreground"
            >
              {activeFilterCount > 99 ? '99+' : activeFilterCount}
            </span>
          ) : null}
        </div>
        <ResultsSortMenu currentSort={sort} onSortChange={onSortChange} isLoading={isLoading} />
      </div>
    </div>
  );
}

function buildAriaLabel(count: number): string {
  if (count <= 0) return 'Filters';
  if (count > 99) return 'Filters, 99+ active';
  return `Filters, ${count} active`;
}
